import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import debounce from 'lodash.debounce';

let scrollTriggerReady = false;
let lenisTickerCallback = null;
let lenisScrollHandler = null;
let lenisInstance = null;
let resizeHandler = null;
let scrollRestorationSyncHandler = null;
let scrollRestorationLoadHandler = null;

export const prefersReducedMotion = () =>
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function parseNumericDataAttr(element, attr, fallback) {
    const value = element?.dataset?.[attr];

    if (value === undefined || value === '') {
        return fallback;
    }

    const parsed = Number.parseFloat(value);

    return Number.isFinite(parsed) ? parsed : fallback;
}

export function markGsapReady(elements) {
    const list = gsap.utils.toArray(elements);

    list.forEach(element => {
        if (element?.dataset) {
            element.dataset.mdaGsapReady = '1';
        }
    });
}

export function markGsapDone(elements) {
    const list = gsap.utils.toArray(elements);

    list.forEach(element => {
        if (element?.dataset) {
            element.dataset.mdaGsapDone = '1';
        }
    });
}

export function isGsapDone(element) {
    return element?.dataset?.mdaGsapDone === '1';
}

export function armWillChange(targets) {
    gsap.set(gsap.utils.toArray(targets), {
        willChange: 'transform, opacity',
    });
}

export function disarmWillChange(targets) {
    gsap.set(gsap.utils.toArray(targets), { clearProps: 'willChange' });
}

export function injectFoucStyles() {
    if (
        typeof document === 'undefined' ||
        document.getElementById('mda-gsap-fouc')
    ) {
        return;
    }

    const style = document.createElement('style');
    style.id = 'mda-gsap-fouc';
    style.textContent = `
        [data-mda-gsap-reveal]:not([data-mda-gsap-ready]),
        [data-mda-gsap-stagger]:not([data-mda-gsap-ready]) {
            visibility: hidden;
        }
        [data-mda-gsap-parallax] {
            will-change: transform;
        }
        [data-mda-gsap-slide-under] {
            position: relative;
            z-index: 0;
        }
        [data-mda-gsap-slide-under-content] {
            position: relative;
            z-index: -1;
        }
    `;
    document.head.appendChild(style);
}

export function getScrollY() {
    if (lenisInstance) {
        return lenisInstance.scroll;
    }

    if (typeof window !== 'undefined') {
        return window.scrollY ?? window.pageYOffset ?? 0;
    }

    return 0;
}

export function syncLenisScrollState(lenis, options = {}) {
    const { allowZero = false } = options;

    if (!lenis || typeof window === 'undefined') {
        return;
    }

    const nativeScroll = window.scrollY ?? window.pageYOffset ?? 0;

    // Avoid forcing scroll to 0 before the browser finishes restoration.
    if (
        !allowZero &&
        nativeScroll === 0 &&
        document.readyState !== 'complete'
    ) {
        ScrollTrigger.update();
        return;
    }

    if (Math.abs(lenis.scroll - nativeScroll) > 1) {
        lenis.scrollTo(nativeScroll, { immediate: true });
    }

    ScrollTrigger.update();
    ScrollTrigger.refresh();
}

export function scheduleScrollRestorationSync(lenis) {
    if (!lenis || typeof window === 'undefined') {
        return;
    }

    const sync = () => syncLenisScrollState(lenis, { allowZero: true });

    if (scrollRestorationLoadHandler) {
        window.removeEventListener('load', scrollRestorationLoadHandler);
    }

    scrollRestorationLoadHandler = () => {
        sync();
        requestAnimationFrame(sync);
    };
    window.addEventListener('load', scrollRestorationLoadHandler, {
        once: true,
    });

    if (scrollRestorationSyncHandler) {
        window.removeEventListener('pageshow', scrollRestorationSyncHandler);
    }

    scrollRestorationSyncHandler = () => {
        requestAnimationFrame(sync);
    };
    window.addEventListener('pageshow', scrollRestorationSyncHandler);

    if (document.readyState === 'complete') {
        requestAnimationFrame(sync);
    }
}

export function setupScrollTrigger(lenis) {
    if (scrollTriggerReady) {
        return;
    }

    gsap.registerPlugin(ScrollTrigger);

    ScrollTrigger.config({
        limitCallbacks: true,
        ignoreMobileResize: true,
    });

    scrollTriggerReady = true;

    if (lenis) {
        lenisInstance = lenis;

        ScrollTrigger.scrollerProxy(document.documentElement, {
            scrollTop(value) {
                if (arguments.length) {
                    lenis.scrollTo(value, { immediate: true });
                }

                return lenis.scroll;
            },
            getBoundingClientRect() {
                return {
                    top: 0,
                    left: 0,
                    width: window.innerWidth,
                    height: window.innerHeight,
                };
            },
        });

        lenisScrollHandler = ScrollTrigger.update;
        lenis.on('scroll', lenisScrollHandler);

        lenisTickerCallback = time => {
            lenis.raf(time * 1000);
        };

        gsap.ticker.add(lenisTickerCallback);
        gsap.ticker.lagSmoothing(0);

        ScrollTrigger.refresh();
    }

    if (typeof window !== 'undefined') {
        resizeHandler = debounce(() => {
            ScrollTrigger.refresh();
        }, 200);

        window.addEventListener('resize', resizeHandler, { passive: true });
    }
}

export function teardownScrollTrigger() {
    if (lenisInstance && lenisScrollHandler) {
        lenisInstance.off('scroll', lenisScrollHandler);
    }

    if (lenisTickerCallback) {
        gsap.ticker.remove(lenisTickerCallback);
        lenisTickerCallback = null;
    }

    if (typeof window !== 'undefined' && resizeHandler) {
        window.removeEventListener('resize', resizeHandler);
        resizeHandler = null;
    }

    if (typeof window !== 'undefined' && scrollRestorationLoadHandler) {
        window.removeEventListener('load', scrollRestorationLoadHandler);
        scrollRestorationLoadHandler = null;
    }

    if (typeof window !== 'undefined' && scrollRestorationSyncHandler) {
        window.removeEventListener('pageshow', scrollRestorationSyncHandler);
        scrollRestorationSyncHandler = null;
    }

    lenisScrollHandler = null;
    lenisInstance = null;
    scrollTriggerReady = false;
}
